// Import necessary types and constants
import * as consts from "./portalConsts";
import type { IPortalClient } from "../clients/iPortalClient";
import type { PagedResult } from "../PagedResult";
import type { VisitBalance } from "../results/VisitBalance";
import type { VisitResults } from "../results/VisitResults";
import type { Patient } from "../Patient";
import type { TestHistory } from "../portal/TestHistory";
import type { PatientTests } from "../portal/PatientTests";
import type { Visit } from "../portal/Visit";

export class ProdCoreClient implements IPortalClient {
  private baseUrl: string;
  private token: string | null;

  // Constructor to initialize base URL and token
  constructor(baseUrl: string, token: string | null) {
    this.baseUrl = baseUrl;
    this.token = token;
  }

  // Fetches laboratory logo in the specified format
  public async fetchLaboratoryLogo(
    format: "svg" | "png",
    ac: AbortSignal | null
  ): Promise<string> {
    const fetchUrl = `${this.baseUrl}${consts.getLaboratoryLogo(format)}`;
    const response = await fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch the laboratory logo");
    }

    // Convert Blob to Base64 string
    const blob = await response.blob();
    return await this.blobToBase64(blob);
  }

  // Helper method to convert Blob to Base64
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;

      // Converts to Base64
      reader.readAsDataURL(blob);
    });
  }

  // Fetches laboratory name based on the current locale
  public async fetchLaboratoryName(
    locale: string,
    ac: AbortSignal | null
  ): Promise<string> {
    const fetchUrl = `${this.baseUrl}${consts.getLaboratoryName(locale)}`;
    const data = await $fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return data as string;
  }

  // Fetches visits based on the page number and page size
  public async fetchVisits(
    pageNumber: number,
    pageSize: number,
    ac: AbortSignal | null,
    filter?: string
  ): Promise<PagedResult<Visit>> {
    const fetchUrl = `${this.baseUrl}${consts.getVisits(
      pageNumber,
      pageSize,
      filter
    )}`;
    const data = await $fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return data as PagedResult<Visit>;
  }

  // Fetches results by visit ID
  public async fetchResultsByVisitId(
    visitId: number,
    ac: AbortSignal | null
  ): Promise<VisitResults[]> {
    const fetchUrl = `${this.baseUrl}${consts.getResultsByVisitId(visitId)}`;
    const data = await $fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return data as VisitResults[];
  }

  // Fetches balance by visit ID
  public async fetchBalanceByVisitId(
    visitId: number,
    ac: AbortSignal | null
  ): Promise<VisitBalance[]> {
    const fetchUrl = `${this.baseUrl}${consts.getBalanceByVisitId(visitId)}`;
    const data = await $fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return data as VisitBalance[];
  }

  // Fetches patient by its ID
  public async fetchPatientById(
    patientId: number,
    ac: AbortSignal | null
  ): Promise<Patient[]> {
    const fetchUrl = `${this.baseUrl}${consts.getPatient(patientId)}`;
    const data = await $fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return data as Patient[];
  }

  // Fetches patient tests by patient ID
  public async fetchTestsByPatientId(
    patientId: number,
    ac: AbortSignal | null
  ): Promise<PatientTests[]> {
    const fetchUrl = `${this.baseUrl}${consts.getPatientTests(patientId)}`;
    const data = await $fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return data as PatientTests[];
  }

  // Fetches test results over time
  public async fetchTestResults(
    patientId: number,
    testId: number,
    ac: AbortSignal | null
  ): Promise<TestHistory[]> {
    const fetchUrl = `${this.baseUrl}${consts.getTestResults(
      patientId,
      testId
    )}`;
    const data = await $fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return data as TestHistory[];
  }

  // Fetches patient visits by patient ID
  public async fetchVisitsByPatientId(
    patientId: number,
    pageNumber: number,
    pageSize: number,
    ac: AbortSignal | null
  ): Promise<PagedResult<Visit>> {
    const fetchUrl = `${this.baseUrl}${consts.getPatientVisits(
      patientId,
      pageNumber,
      pageSize
    )}`;
    const data = await $fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return data as PagedResult<Visit>;
  }

  // Fetches laboratory terms of use
  public async fetchLaboratoryTermsOfUse(
    ac: AbortSignal | null
  ): Promise<string> {
    const fetchUrl = `${this.baseUrl}${consts.getLaboratoryTermsOfUse()}`;
    const data = await $fetch(fetchUrl, {
      signal: ac,
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return data as string;
  }
}
